/**
 * Hierarchical Browser Component
 * Three-level dropdown navigation for browsing transcription content
 */

import React, { useCallback, useMemo } from 'react';
import { Select, Stack, useTheme, textStyles } from '@fti/design-system';

export interface BrowserSelection {
  source: string | null;
  session: string | null;
  title: string | null;
}

export interface BrowserOption {
  value: string;
  label: string;
}

export interface HierarchicalBrowserProps {
  sources: string[] | BrowserOption[];
  sessions: string[] | BrowserOption[];
  titles: string[] | BrowserOption[];
  selection: BrowserSelection;
  onSelectionChange: (selection: BrowserSelection) => void;
  isLoadingSources?: boolean;
  isLoadingSessions?: boolean;
  isLoadingTitles?: boolean;
  disabled?: boolean;
}

/**
 * Three-level hierarchical browser for navigating transcription content
 * Source > Session > Title
 */
export function HierarchicalBrowser({
  sources,
  sessions,
  titles,
  selection,
  onSelectionChange,
  isLoadingSources = false,
  isLoadingSessions = false,
  isLoadingTitles = false,
  disabled = false,
}: HierarchicalBrowserProps) {
  const { theme } = useTheme();

  const textColor = theme.colors.text.primary;

  const handleSourceChange = useCallback((value: string) => {
    onSelectionChange({
      source: value || null,
      session: null,
      title: null,
    });
  }, [onSelectionChange]);

  const handleSessionChange = useCallback((value: string) => {
    onSelectionChange({
      ...selection,
      session: value || null,
      title: null,
    });
  }, [selection, onSelectionChange]);

  const handleTitleChange = useCallback((value: string) => {
    onSelectionChange({
      ...selection,
      title: value || null,
    });
  }, [selection, onSelectionChange]);

  const buildOptions = (items: string[] | BrowserOption[], placeholder: string) => [
    { value: '', label: placeholder },
    ...items.map((item) =>
      typeof item === 'string' ? { value: item, label: item } : item
    ),
  ];

  return (
    <Stack gap="md">
      {/* Source Selection */}
      <Stack gap="xs">
        <label style={{ ...textStyles.label, color: textColor }}>
          Source / Streamer
        </label>
        <Select
          value={selection.source || ''}
          onChange={handleSourceChange}
          disabled={disabled || isLoadingSources}
          placeholder={isLoadingSources ? 'Loading sources...' : 'Select a source'}
          options={buildOptions(sources, isLoadingSources ? 'Loading...' : 'Select a source')}
        />
      </Stack>

      {/* Session Selection */}
      <Stack gap="xs">
        <label style={{ ...textStyles.label, color: textColor }}>
          Session / Date
        </label>
        <Select
          value={selection.session || ''}
          onChange={handleSessionChange}
          disabled={disabled || isLoadingSessions || !selection.source}
          placeholder={
            !selection.source
              ? 'Select a source first'
              : isLoadingSessions
              ? 'Loading sessions...'
              : 'Select a session'
          }
          options={buildOptions(
            sessions,
            !selection.source
              ? 'Select a source first'
              : isLoadingSessions
              ? 'Loading...'
              : 'Select a session'
          )}
        />
      </Stack>

      {/* Title Selection */}
      <Stack gap="xs">
        <label style={{ ...textStyles.label, color: textColor }}>
          Stream Title
        </label>
        <Select
          value={selection.title || ''}
          onChange={handleTitleChange}
          disabled={disabled || isLoadingTitles || !selection.session}
          placeholder={
            !selection.session
              ? 'Select a session first'
              : isLoadingTitles
              ? 'Loading titles...'
              : 'Select a stream'
          }
          options={buildOptions(
            titles,
            !selection.session
              ? 'Select a session first'
              : isLoadingTitles
              ? 'Loading...'
              : 'Select a stream'
          )}
        />
      </Stack>
    </Stack>
  );
}

export default HierarchicalBrowser;
